import { useState, useEffect, useMemo } from 'react';
import { DashboardLayout } from '@/components/layout/DashboardLayout';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Badge } from '@/components/ui/badge';
import { Mail, Phone, Edit, Trash2, Building, MessageCircle } from 'lucide-react';
import { useSearchParams } from 'react-router-dom';
import { BusinessCardScanner } from '@/components/contacts/BusinessCardScanner';
import { ContactFormWithScanner } from '@/components/contacts/ContactFormWithScanner';
import { CommunicationDialog } from '@/components/contacts/CommunicationDialog';
import { ContactsFilterBar } from '@/components/contacts/ContactsFilterBar';
import { ContactsHeader } from '@/components/contacts/ContactsHeader';
import { ContactsStats } from '@/components/contacts/ContactsStats';
import { DeleteConfirmDialog } from '@/components/ui/delete-confirm-dialog';
import { TableSkeleton } from '@/components/ui/table-skeleton';
import { PaginationControls } from '@/components/ui/pagination-controls';
import { useContacts, Contact } from '@/hooks/useContacts';
import { useContactFilters } from '@/hooks/useContactFilters';
import { useContactActions } from '@/hooks/useContactActions';
import { useDialogState } from '@/hooks/useDialogState';
import { useClientPagination } from '@/hooks/useClientPagination';
import { supabase } from '@/integrations/supabase/client';
import type { BusinessCardScanResult } from '@/hooks/useBusinessCardScanner';

const contactTypeColors = {
  lead: 'bg-blue-100 text-blue-800 border-blue-200',
  client: 'bg-green-100 text-green-800 border-green-200',
  vendor: 'bg-purple-100 text-purple-800 border-purple-200',
  contractor: 'bg-orange-100 text-orange-800 border-orange-200'
};

const statusColors = {
  new: 'bg-gray-100 text-gray-800 border-gray-200',
  contacted: 'bg-yellow-100 text-yellow-800 border-yellow-200',
  qualified: 'bg-blue-100 text-blue-800 border-blue-200',
  active: 'bg-green-100 text-green-800 border-green-200',
  inactive: 'bg-red-100 text-red-800 border-red-200'
};

export default function Contacts() {
  const [searchParams, setSearchParams] = useSearchParams();
  const { contacts, loading, deleteContact, refetch } = useContacts();
  const { filters, updateFilters, clearFilters, activeFilterCount, applyFilters } = useContactFilters();
  const formDialog = useDialogState<Contact>();
  const [deletingContact, setDeletingContact] = useState<Contact | null>(null);
  const [addingSamples, setAddingSamples] = useState(false);
  const [communicationContact, setCommunicationContact] = useState<Contact | null>(null);
  const [communicationCounts, setCommunicationCounts] = useState<Record<string, number>>({});
  const [totalCommunications, setTotalCommunications] = useState(0);
  const [generatingSamples, setGeneratingSamples] = useState(false);
  const { addSampleContacts, generateSampleCommunications } = useContactActions(refetch);
  const [scannerOpen, setScannerOpen] = useState(false);
  const [scannedData, setScannedData] = useState<BusinessCardScanResult | null>(null);

  // Handle deep link action for app shortcuts
  useEffect(() => {
    const action = searchParams.get('action');
    if (action === 'create') {
      formDialog.open();
      // Clear params after opening dialog
      searchParams.delete('action');
      setSearchParams(searchParams, { replace: true });
    }
  }, [searchParams, setSearchParams, formDialog]);

  useEffect(() => {
    if (contacts.length > 0) {
      fetchCommunicationCounts();
    }
  }, [contacts]);

  const fetchCommunicationCounts = async () => {
    const { data, error } = await supabase
      .from('contact_communications')
      .select('contact_id', { count: 'exact' });

    if (!error && data) {
      const counts: Record<string, number> = {};
      let total = 0;
      data.forEach((comm) => {
        counts[comm.contact_id] = (counts[comm.contact_id] || 0) + 1;
        total++;
      });
      setCommunicationCounts(counts);
      setTotalCommunications(total);
    }
  };

  // Apply filters and pagination - moved before loading check to avoid hooks order violation
  const filteredContacts = applyFilters(contacts ?? []);

  // Client-side pagination using shared hook
  const {
    currentPage,
    pageSize,
    totalPages,
    paginatedItems: paginatedContacts,
    setCurrentPage,
    setPageSize,
    hasNextPage,
    hasPreviousPage,
  } = useClientPagination({ items: filteredContacts });

  // Calculate stats - moved before loading check
  const contactStats = useMemo(() => ({
    total: (contacts ?? []).length,
    leads: (contacts ?? []).filter(c => c.contact_type === 'lead').length,
    clients: (contacts ?? []).filter(c => c.contact_type === 'client').length,
    vendors: (contacts ?? []).filter(c => c.contact_type === 'vendor').length,
    contractors: (contacts ?? []).filter(c => c.contact_type === 'contractor').length,
    active: (contacts ?? []).filter(c => c.status === 'active').length
  }), [contacts]);

  const handleDelete = async (contact: Contact) => {
    await deleteContact(contact.id);
    setDeletingContact(null);
  };

  const handleScanComplete = (result: BusinessCardScanResult) => {
    setScannedData(result);
    setScannerOpen(false);
    formDialog.open();
  };

  const handleRescan = () => {
    formDialog.close();
    setScannedData(null);
    setScannerOpen(true);
  };

  const handleFormClose = () => {
    formDialog.close();
    setScannedData(null);
  };

  if (loading) {
    return (
      <DashboardLayout>
        <div className="space-y-6">
          <ContactsHeader
            contactsCount={0}
            totalCommunications={0}
            addingSamples={false}
            generatingSamples={false}
            onAddSamples={() => {}}
            onGenerateSamples={() => {}}
            onAddContact={() => {}}
            onScanBusinessCard={() => {}}
          />
          <TableSkeleton rows={10} columns={5} />
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <div className="space-y-6">
        <ContactsHeader
          contactsCount={contacts.length}
          totalCommunications={totalCommunications}
          addingSamples={addingSamples}
          generatingSamples={generatingSamples}
          onAddSamples={() => addSampleContacts(setAddingSamples)}
          onGenerateSamples={() => generateSampleCommunications(contacts, setGeneratingSamples, fetchCommunicationCounts)}
          onAddContact={() => formDialog.open()}
          onScanBusinessCard={() => setScannerOpen(true)}
        />

        <ContactsStats
          total={contactStats.total}
          active={contactStats.active}
          clients={contactStats.clients}
          leads={contactStats.leads}
        />

        <ContactsFilterBar
          filters={filters}
          onFilterChange={updateFilters}
          onClearFilters={clearFilters}
          activeFilterCount={activeFilterCount}
          allContacts={contacts}
        />

        <Card>
          <CardHeader>
            <CardTitle>
              {activeFilterCount > 0 ? (
                <span>Contacts ({filteredContacts.length} of {contacts.length})</span>
              ) : (
                'All Contacts'
              )}
            </CardTitle>
            <CardDescription>A comprehensive list of all your business contacts</CardDescription>
          </CardHeader>
          <CardContent>
            {contacts.length === 0 ? (
              <div className="text-center py-8">
                <Building className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
                <h3 className="text-lg font-medium">No contacts yet</h3>
                <p className="text-muted-foreground mb-4">Get started by adding your first contact or importing sample data</p>
              </div>
            ) : (
              <>
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Name</TableHead>
                      <TableHead>Email</TableHead>
                      <TableHead>Phone</TableHead>
                      <TableHead>Company</TableHead>
                      <TableHead>Type</TableHead>
                      <TableHead>Status</TableHead>
                      <TableHead>Communications</TableHead>
                      <TableHead className="text-right">Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {paginatedContacts.map((contact) => (
                      <TableRow key={contact.id}>
                        <TableCell className="font-medium">{contact.name}</TableCell>
                        <TableCell>
                          {contact.email && (
                            <div className="flex items-center gap-2">
                              <Mail className="h-4 w-4 text-muted-foreground" />
                              {contact.email}
                            </div>
                          )}
                        </TableCell>
                        <TableCell>
                          {contact.phone && (
                            <div className="flex items-center gap-2">
                              <Phone className="h-4 w-4 text-muted-foreground" />
                              {contact.phone}
                            </div>
                          )}
                        </TableCell>
                        <TableCell>
                          {contact.company 
                            ? typeof contact.company === 'string' 
                              ? contact.company 
                              : contact.company.name
                            : '-'}
                        </TableCell>
                        <TableCell>
                          <Badge variant="outline" className={contactTypeColors[contact.contact_type]}>
                            {contact.contact_type}
                          </Badge>
                        </TableCell>
                        <TableCell>
                          <Badge variant="outline" className={statusColors[contact.status]}>
                            {contact.status}
                          </Badge>
                        </TableCell>
                        <TableCell>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => setCommunicationContact(contact)}
                            className="gap-2"
                          >
                            <MessageCircle className="h-4 w-4" />
                            <span className="text-xs">{communicationCounts[contact.id] || 0}</span>
                          </Button>
                        </TableCell>
                        <TableCell className="text-right">
                          <div className="flex justify-end gap-2">
                            <Button variant="ghost" size="sm" onClick={() => formDialog.open(contact)}>
                              <Edit className="h-4 w-4" />
                            </Button>
                            <Button variant="ghost" size="sm" onClick={() => setDeletingContact(contact)}>
                              <Trash2 className="h-4 w-4" />
                            </Button>
                          </div>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
                
                {filteredContacts.length > 0 && (
                  <PaginationControls
                    currentPage={currentPage}
                    totalPages={totalPages}
                    totalCount={filteredContacts.length}
                    pageSize={pageSize}
                    onPageChange={setCurrentPage}
                    onPageSizeChange={setPageSize}
                    hasNextPage={hasNextPage}
                    hasPreviousPage={hasPreviousPage}
                  />
                )}
              </>
            )}
          </CardContent>
        </Card>

        <BusinessCardScanner
          open={scannerOpen}
          onOpenChange={setScannerOpen}
          onScanComplete={handleScanComplete}
        />

        <ContactFormWithScanner
          open={formDialog.isOpen}
          onOpenChange={(open) => !open && handleFormClose()}
          contact={formDialog.data}
          onSubmit={handleFormClose}
          scannedData={scannedData || undefined}
          onRescan={scannedData ? handleRescan : undefined}
        />

        <DeleteConfirmDialog
          open={!!deletingContact}
          onOpenChange={(open) => !open && setDeletingContact(null)}
          onConfirm={() => deletingContact && handleDelete(deletingContact)}
          title="Delete Contact"
          description={`Are you sure you want to delete ${deletingContact?.name}? This action cannot be undone.`}
        />

        {communicationContact && (
          <CommunicationDialog
            contact={communicationContact}
            open={!!communicationContact}
            onOpenChange={(open) => !open && setCommunicationContact(null)}
          />
        )}
      </div>
    </DashboardLayout>
  );
}
